import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import CustomRadioButton from "../../Components/Form Fields/radio-button.js";
import PrimaryButton from "../../Components/Buttons/primary-button.js";

import { renderDeleteConnectedAccount } from "../../Organisms/Modals/Bank Account/delete-connected-account.js";

import { useModalContext } from "../../Contexts/ModalContext";
import { useAppUtilityContext } from "../../Contexts/AppUtilityContext.js";

import { useConnectedAccounts, useConnectedAccountSelection, useGetRecurringDeposit } from "../../CustomHooks/Queries";

import { ArrowLeftCircle, AddCircle, ChevronLeft, Trash } from "../../Assets/Icons";

import useConnectBankAccount from "../../CustomHooks/useConnectBankAccount.js";

import "./connected-accounts.scss";
import "./connected-accounts-responsive.scss";

const BankAccountItem = ({ account, isDefaultAccount, selected, selectable, setAccountAsDefault, onSelect }) => {
  const modalContext = useModalContext();

  const handleResetConnectedAccount = (e) => {
    e.stopPropagation();
    renderDeleteConnectedAccount(modalContext, account);
  };

  const selectCardForPayment = (account) => {
    if (selectable) {
      onSelect?.(account.account_id);
    }
  };

  return (
    <div
      className={`bank-account-item ${selectable ? "selectable" : ""} ${selected ? "selected" : ""}`}
      onClick={() => selectCardForPayment(account)}
    >
      <div className="info">
        {selectable && <CustomRadioButton checked={selected} onChange={() => selectCardForPayment(account)} />}
        {!selectable && (
          <div className="bank-icon-box">
            {!!account.logo_url_base64_encoded ? (
              <img className="bank-logo" src={`data:image/png;base64,${account.logo_url_base64_encoded}`} alt="" />
            ) : (
              <svg width="22" height="22" viewBox="0 0 640 640" fill="none">
                <path d="M333.2 75.9C325.2 70.7 314.9 70.7 306.9 75.9L74.9 227.9C66 233.7 62 244.7 65 254.9C68 265.1 77.4 272 88 272L128 272L128 432L120 432C106.7 432 96 442.7 96 456C96 469.3 106.7 480 120 480L520 480C533.3 480 544 469.3 544 456C544 442.7 533.3 432 520 432L512 432L512 272L552 272C562.6 272 572 265 575 254.9C578 244.8 574 233.8 565.2 228L333.2 76zM240 432L176 432L176 272L240 272L240 432zM352 432L288 432L288 272L352 272L352 432zM464 432L400 432L400 272L464 272L464 432zM292.3 224L168.4 224L320 124.7L471.6 224L347.7 224C350.4 219.3 352 213.8 352 208C352 190.3 337.7 176 320 176C302.3 176 288 190.3 288 208C288 213.8 289.6 219.3 292.3 224zM64 552C64 565.3 74.7 576 88 576L552 576C565.3 576 576 565.3 576 552C576 538.7 565.3 528 552 528L88 528C74.7 528 64 538.7 64 552z" fill="#54FF99" />
              </svg>
            )}
          </div>
        )}
        <div className="account-details">
          <h4 className="account-name">{account.name ?? ""}</h4>
          <span className="account-number">••••{account.mask ?? "****"}</span>
        </div>
      </div>
      <div className="actions">
        {selectable ? (
          <button className="delete" onClick={handleResetConnectedAccount} aria-label="Remove account">
            <Trash />
          </button>
        ) : (
          <>
            {isDefaultAccount ? (
              <span className="default-badge">Default</span>
            ) : (
              <button className="set-default" onClick={setAccountAsDefault}>
                Set as default
              </button>
            )}
            <button className="delete" onClick={handleResetConnectedAccount} aria-label="Remove account">
              <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
                <path d="M1 4H15M5 4V3C5 1.895 5.895 1 7 1H9C10.105 1 11 1.895 11 3V4M13 4V15C13 16.105 12.105 17 11 17H5C3.895 17 3 16.105 3 15V4M6 8V13M10 8V13" stroke="#D94545" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default function ConnectedAccountsPage() {
  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, []);

  return (
    <div className={`connected-accounts${animReady ? " anim-ready" : ""}`}>
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <ConnectedAccounts />
          </div>
        </div>
      </section>
    </div>
  );
}

export const ConnectedAccounts = ({ presentedForSelection = false, openPlaidOnMount = false }) => {
  const { popModalStack, setModalClassName } = useModalContext();

  const { appUtilityProps, setAppUtilityProps } = useAppUtilityContext();

  const navigate = useNavigate();

  const { isFetching: isFetchingConnectedAccounts, data: connectedAccounts } = useConnectedAccounts();

  const { isFetching: isFetchingConnectedAccountSelection, data: connectedAccountSelection } =
    useConnectedAccountSelection();

  const { isLoading: isLoadingRecurringDeposit } = useGetRecurringDeposit();

  const { isSavingAccount, plaidReady, openPlaid, saveAccountAsDefaultAccount } = useConnectBankAccount({
    onInitiatingSave: () => presentedForSelection && setModalClassName("rendering-page-content connected-accounts-modal"),
    onExit: () => presentedForSelection && setModalClassName("rendering-page-content connected-accounts-modal"),
  });

  const connectedAccountItems = useMemo(() => {
    const accounts = connectedAccounts?.data?.data?.accounts || [];
    return accounts.map((account, index) => (
      <BankAccountItem
        key={account.account_id || index}
        account={account}
        isDefaultAccount={connectedAccountSelection?.data?.data?.selected === account.account_id}
        setAccountAsDefault={() => saveAccountAsDefaultAccount({ account_id: account.account_id })}
        selectable={presentedForSelection}
        selected={account.account_id === (appUtilityProps?.selectedAccountId ?? accounts[0]?.account_id)}
        onSelect={(account_id) => setAppUtilityProps((p) => ({ ...p, selectedAccountId: account_id }))}
      />
    )); //eslint-disable-next-line
  }, [
    connectedAccounts?.data?.data?.accounts,
    connectedAccountSelection?.data?.data?.selected,
    saveAccountAsDefaultAccount,
    presentedForSelection,
    appUtilityProps?.selectedAccountId,
  ]);

  const isConnected = useMemo(() => {
    return connectedAccountItems.length > 0;
  }, [connectedAccountItems]);

  const handleAddBank = useCallback(
    () => {
      if (presentedForSelection) {
        setModalClassName("d-none");
      }
      openPlaid();
    }, //eslint-disable-next-line
    [openPlaid, presentedForSelection]
  );

  useEffect(() => {
    if (openPlaidOnMount && plaidReady) {
      handleAddBank();
    }
  }, [openPlaidOnMount, plaidReady, handleAddBank]);

  const isFetching =
    isFetchingConnectedAccounts || isFetchingConnectedAccountSelection || isSavingAccount || isLoadingRecurringDeposit;

  const showEmptyState = !isConnected && !isFetching && plaidReady;

  return (
    <>
      {!presentedForSelection && (
        showEmptyState ? (
          <div className="section-header empty-state-header">
            <button className="page-back-btn" onClick={() => navigate("/home")} aria-label="Go back to home">
              <ChevronLeft />
            </button>
          </div>
        ) : (
          <div className="section-header ca-header">
            <button className="page-back-btn" onClick={() => navigate("/home")} aria-label="Go back to home">
              <ChevronLeft />
            </button>
            <h3 className="section-title">Connected Accounts</h3>
            {!plaidReady ? (
              <CircularProgress />
            ) : (
              <button className="ca-add-button" onClick={handleAddBank} aria-label="Add bank account">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1V15M1 8H15" stroke="#888ca0" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        )
      )}
      {isFetching || (!isConnected && !plaidReady) ? (
        <div className="loading-content">
          <CircularProgress />
        </div>
      ) : isConnected ? (
        <div className="content connected-bank-accounts">
          <div className="top-container">
            <div className="hero-card">
              {connectedAccountItems}
            </div>
            {presentedForSelection && (
              <button className="add-account" onClick={handleAddBank}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="9" stroke="#FFFFFF" strokeWidth="1.3" />
                  <path d="M10 6V14M6 10H14" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
                Add New Account
              </button>
            )}
          </div>
          {presentedForSelection && (
            <div className="bottom-container stick-to-modal-bottom">
              <PrimaryButton onClick={() => popModalStack(1)}>Confirm</PrimaryButton>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state-content">
          <div className="empty-state-hero">
            <svg className="hero-icon" width="48" height="48" viewBox="0 0 640 640" fill="none">
              <path d="M333.2 75.9C325.2 70.7 314.9 70.7 306.9 75.9L74.9 227.9C66 233.7 62 244.7 65 254.9C68 265.1 77.4 272 88 272L128 272L128 432L120 432C106.7 432 96 442.7 96 456C96 469.3 106.7 480 120 480L520 480C533.3 480 544 469.3 544 456C544 442.7 533.3 432 520 432L512 432L512 272L552 272C562.6 272 572 265 575 254.9C578 244.8 574 233.8 565.2 228L333.2 76zM240 432L176 432L176 272L240 272L240 432zM352 432L288 432L288 272L352 272L352 432zM464 432L400 432L400 272L464 272L464 432zM292.3 224L168.4 224L320 124.7L471.6 224L347.7 224C350.4 219.3 352 213.8 352 208C352 190.3 337.7 176 320 176C302.3 176 288 190.3 288 208C288 213.8 289.6 219.3 292.3 224zM64 552C64 565.3 74.7 576 88 576L552 576C565.3 576 576 565.3 576 552C576 538.7 565.3 528 552 528L88 528C74.7 528 64 538.7 64 552z" fill="var(--accent)" />
            </svg>
            <div className="hero-text">
              <h3 className="hero-title">Connect your bank</h3>
              <p className="hero-subtitle">Always have funds ready to spend and earn cash back.</p>
            </div>
          </div>
          <div className="empty-state-steps">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-text">
                <h4>Link your bank</h4>
                <p>Securely in under a minute</p>
              </div>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-text">
                <h4>Transfer funds</h4>
                <p>Move money to Stake instantly or on a schedule</p>
              </div>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-text">
                <h4>Earn cash back</h4>
                <p>Earn cash back every time you spend</p>
              </div>
            </div>
          </div>
          <div className="empty-state-cta">
            <button className="cta-button" onClick={handleAddBank}>
              Set up in 1 minute
            </button>
            <div className="trust-badges">
              <span className="dot">&middot;</span>
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                <rect x="3" y="8" width="12" height="8" rx="1.5" fill="rgba(84,255,153,0.15)" stroke="var(--accent)" strokeWidth="1.2" />
                <path d="M6 8V5.5C6 3.84 7.34 2.5 9 2.5C10.66 2.5 12 3.84 12 5.5V8" stroke="var(--accent)" strokeWidth="1.2" strokeLinecap="round" />
                <circle cx="9" cy="12" r="1" fill="var(--accent)" />
              </svg>
              <span>256-bit encryption</span>
              <span className="dot">&middot;</span>
              <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                <path d="M9 1.5L3 4.5V8.25C3 12.35 5.56 16.17 9 17.25C12.44 16.17 15 12.35 15 8.25V4.5L9 1.5Z" fill="rgba(84,255,153,0.15)" stroke="var(--accent)" strokeWidth="1.2" strokeLinejoin="round" />
                <path d="M6.5 9L8 10.5L11.5 7" stroke="var(--accent)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Powered by Plaid</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
